/**
 * FloatingAreaContext
 *
 * Contexto compartido que permite coordinar el posicionamiento vertical
 * entre el FloatingButtonsPanel (WhatsApp, ayuda, etc.) y el AssistantWidget.
 *
 * Cuando FloatingButtonsPanel está montado, reporta la altura que ocupa
 * para que AssistantWidget se desplace hacia arriba y no se solapen.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

interface FloatingAreaState {
  /** Altura en px que ocupa el FloatingButtonsPanel (0 si no está montado). */
  panelHeight: number;
  /** Registra / actualiza la altura del panel flotante. */
  setPanelHeight: (height: number) => void;
}

const FloatingAreaContext = createContext<FloatingAreaState>({
  panelHeight: 0,
  setPanelHeight: () => {},
});

export const FloatingAreaProvider = ({ children }: { children: ReactNode }) => {
  const [panelHeight, setPanelHeightRaw] = useState(0);

  const setPanelHeight = useCallback((height: number) => {
    setPanelHeightRaw(height);
  }, []);

  const value = useMemo(
    () => ({ panelHeight, setPanelHeight }),
    [panelHeight, setPanelHeight],
  );

  return (
    <FloatingAreaContext.Provider value={value}>
      {children}
    </FloatingAreaContext.Provider>
  );
};

/**
 * Hook para consumir la altura del panel flotante.
 * Devuelve panelHeight y setPanelHeight.
 */
export const useFloatingArea = () => useContext(FloatingAreaContext);
